import React, { useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import {
  saveSkills,
  loadSkills,
  saveCerts,
  loadCerts,
  saveProofs,
  loadProofs,
  uploadProofFile,
  setEmailMuteAll,
  loadEmailPrefs,
  setPublicProfile,
  loadPublicProfile,
  removePublicProfile,
} from "../../lib/firebase";
import { notifyCertAchieved } from "../../lib/functions";
import { useAuth } from "../../context/AuthContext";
import {
  Section,
  SectionLabel,
  SectionTitle,
  SectionDesc,
  TeamCareerGrid,
  TeamCard,
  TeamName,
  TeamRole,
  TeamPath,
  PathNode,
  PathArrow,
  TrackSwitch,
  TeamCerts,
  MiniCert,
  GrowthText,
  GrowthBox,
  GrowthTitle,
  Honest,
} from "./CareerPath.styles";
import CareerPathHero from "./CareerPathHero";
import LaneViewer from "./LaneViewer";
import CertificationsGrid from "./CertificationsGrid";
import SkillsInventory from "./SkillsInventory";

// ── Data ───────────────────────────────────────────────────────────

const teamMembers = [
  {
    name: "Fabio Palumbo",
    role: "Founder & DevOps Architect",
    currentGrade: "SC",
    track: "DevOps",
    path: [
      { grade: "JT", track: "DevOps" },
      { grade: "EN", track: "DevOps" },
      { grade: "SE", track: "DevOps" },
      { grade: "SE", track: "SRE", switched: true },
      { grade: "SC", track: "DevOps", switched: true },
    ],
    certs: ["AWS SA Pro", "CKA", "Terraform"],
    note: "Jumped from DevOps to SRE at senior level, then back to DevOps as consultant. Kept the SRE skills, gained the breadth.",
  },
  {
    name: "Your Next Hire",
    role: "Junior DevOps Engineer",
    currentGrade: "JT",
    track: "DevOps",
    path: [
      { grade: "JT", track: "DevOps" },
    ],
    certs: [],
    note: "Starts here. We'll help them figure out the rest.",
  },
];

const STORAGE_KEY = "dioscuro-career-skills";
const CERTS_STORAGE_KEY = "dioscuro-career-certs";
const PROOFS_STORAGE_KEY = "dioscuro-career-proofs";

// ── Component ──────────────────────────────────────────────────────

const CareerPath = () => {
  const { user, signIn, signOut } = useAuth();
  const [saveMsg, setSaveMsg] = useState("");
  const [emailMuted, setEmailMuted] = useState(false);
  const [skills, setSkills] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [certs, setCerts] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(CERTS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [openProof, setOpenProof] = useState(null);
  const [proofTab, setProofTab] = useState("link");
  const [proofUrl, setProofUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const [proofs, setProofs] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(PROOFS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [openCertProof, setOpenCertProof] = useState(null);
  const [certProofTab, setCertProofTab] = useState("link");
  const [certProofUrl, setCertProofUrl] = useState("");
  const [certUploading, setCertUploading] = useState(false);
  const [profilePublic, setProfilePublic] = useState(false);
  const [copyHint, setCopyHint] = useState("");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    loadSkills(user.uid).then((remote) => {
      if (!cancelled && remote) setSkills(remote);
    });
    loadCerts(user.uid).then((remoteCerts) => {
      if (!cancelled && remoteCerts) setCerts(remoteCerts);
    });
    loadProofs(user.uid).then((remoteProofs) => {
      if (!cancelled && remoteProofs) setProofs(remoteProofs);
    });
    loadEmailPrefs(user.uid).then((prefs) => {
      if (!cancelled && prefs && typeof prefs.muteAll === "boolean") setEmailMuted(prefs.muteAll);
    });
    loadPublicProfile(user.uid).then((p) => {
      if (!cancelled && p) setProfilePublic(true);
    });
    return () => { cancelled = true; };
  }, [user]);

  const handleToggleEmailMute = async () => {
    if (!user) return;
    const next = !emailMuted;
    setEmailMuted(next);
    try {
      await setEmailMuteAll(user.uid, next);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[email-prefs] toggle failed:", err);
      setEmailMuted(!next);
    }
  };

  // Keep publicProfiles/{uid} in sync when toggle is on.
  // Debounced 400ms so rapid edits (typing in proof title, clicking
  // through skill levels) coalesce into one Firestore write.
  useEffect(() => {
    if (!user || !profilePublic) return;
    const t = setTimeout(() => {
      setPublicProfile(user.uid, {
        uid: user.uid,
        displayName: user.displayName || "Member",
        photoURL: user.photoURL || null,
        assessments: skills,
        certs,
        proofs,
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [profilePublic, skills, certs, proofs, user]);

  const handleTogglePublic = async () => {
    if (!user) return;
    if (profilePublic) {
      try {
        await removePublicProfile(user.uid);
        setProfilePublic(false);
      } catch (err) {
        console.error("[profile] off failed:", err);
      }
    } else {
      try {
        await setPublicProfile(user.uid, {
          uid: user.uid,
          displayName: user.displayName || "Member",
          photoURL: user.photoURL || null,
          assessments: skills,
          certs,
          proofs,
        });
        setProfilePublic(true);
      } catch (err) {
        console.error("[profile] on failed:", err);
      }
    }
  };

  const handleCopyShareLink = async () => {
    if (!user || typeof window === "undefined") return;
    const url = `${window.location.origin}/p/?uid=${user.uid}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyHint("COPIED");
      setTimeout(() => setCopyHint(""), 1500);
    } catch (err) {
      console.error("[profile] copy failed:", err);
    }
  };

  // Save to localStorage always, Firebase when logged in
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(skills)); } catch {}
    if (user && Object.keys(skills).length > 0) {
      saveSkills(user.uid, skills).then(() => {
        setSaveMsg("SAVED");
        setTimeout(() => setSaveMsg(""), 2000);
      }).catch(() => {});
    }
  }, [skills, user]);

  useEffect(() => {
    try { localStorage.setItem(CERTS_STORAGE_KEY, JSON.stringify(certs)); } catch {}
    if (user && Object.keys(certs).length > 0) {
      saveCerts(user.uid, certs).catch(() => {});
    }
  }, [certs, user]);

  // Tracks cert names last seen at "achieved" so we only signal the server on a
  // fresh achievement, not on every unrelated proof edit.
  const achievedCertsRef = useRef(new Set());

  useEffect(() => {
    try { localStorage.setItem(PROOFS_STORAGE_KEY, JSON.stringify(proofs)); } catch {}
    if (user && Object.keys(proofs).length > 0) {
      // A cert may become "achieved" via the status toggle or a proof save.
      // Detect newly-achieved certs, persist, THEN (best-effort) ask the server
      // to recompute path milestones — after the write lands so the server
      // reads fresh proofs. Idempotent server-side via xp_events dedup.
      const nowAchieved = Object.keys(proofs).filter((k) => proofs[k] && proofs[k].status === "achieved");
      const prev = achievedCertsRef.current;
      const hasNew = nowAchieved.some((k) => !prev.has(k));
      achievedCertsRef.current = new Set(nowAchieved);
      saveProofs(user.uid, proofs)
        .then(() => { if (hasNew) return notifyCertAchieved(); })
        .catch(() => {});
    }
  }, [proofs, user]);

  const handleProofToggle = (skill) => {
    if (openProof === skill) {
      setOpenProof(null);
    } else {
      setOpenProof(skill);
      setProofTab("link");
      setProofUrl(certs[skill]?.type === "link" ? certs[skill].url : "");
    }
  };

  const handleSaveLink = (skill) => {
    if (!proofUrl) return;
    setCerts((prev) => ({ ...prev, [skill]: { type: "link", url: proofUrl, name: proofUrl } }));
    setOpenProof(null);
  };

  const handleUploadFile = async (skill, file) => {
    if (!file) return;
    if (!user) return;
    setUploading(true);
    try {
      const result = await uploadProofFile(user.uid, skill, file);
      if (result) {
        setCerts((prev) => ({
          ...prev,
          [skill]: { type: "file", url: result.url, name: result.name, path: result.path },
        }));
        setOpenProof(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[proof] upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCert = (skill) => {
    setCerts((prev) => {
      const next = { ...prev };
      delete next[skill];
      return next;
    });
  };

  // ── Certifications-section handlers ─────────────────────────────
  const CERT_STATUS_CYCLE = ["to_be_started", "in_progress", "achieved"];

  const certStatus = (cert) => {
    const override = proofs[cert.name]?.status;
    if (override) return override;
    return cert.achieved ? "achieved" : "to_be_started";
  };

  const handleCertStatusToggle = (cert) => {
    if (!user) return;
    const current = certStatus(cert);
    const idx = CERT_STATUS_CYCLE.indexOf(current);
    const next = CERT_STATUS_CYCLE[(idx + 1) % CERT_STATUS_CYCLE.length];
    setProofs((prev) => ({
      ...prev,
      [cert.name]: { ...(prev[cert.name] || {}), status: next },
    }));
  };

  const handleCertProofToggle = (certName) => {
    if (openCertProof === certName) {
      setOpenCertProof(null);
    } else {
      setOpenCertProof(certName);
      setCertProofTab("link");
      const existing = proofs[certName];
      setCertProofUrl(existing && existing.type === "link" ? existing.url : "");
    }
  };

  const handleCertSaveLink = (certName) => {
    if (!certProofUrl) return;
    setProofs((prev) => ({
      ...prev,
      [certName]: {
        ...(prev[certName] || {}),
        type: "link",
        url: certProofUrl,
        name: certProofUrl,
      },
    }));
    setOpenCertProof(null);
  };

  const handleCertUploadFile = async (certName, file) => {
    if (!file || !user) return;
    setCertUploading(true);
    try {
      const result = await uploadProofFile(user.uid, certName, file);
      if (result) {
        setProofs((prev) => ({
          ...prev,
          [certName]: {
            ...(prev[certName] || {}),
            type: "file",
            url: result.url,
            name: result.name,
            path: result.path,
          },
        }));
        setOpenCertProof(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[cert proof] upload failed:", err);
    } finally {
      setCertUploading(false);
    }
  };

  const handleCertRemoveProof = (certName) => {
    setProofs((prev) => {
      const next = { ...prev };
      const existing = next[certName];
      if (existing) {
        // Keep status, drop attachment fields
        next[certName] = { status: existing.status };
        if (!next[certName].status) delete next[certName];
      }
      return next;
    });
  };

  return (
    <>
      <CareerPathHero />

      {/* Team Career Paths */}
      <Section>
        <Container>
          <SectionLabel>OUR TEAM</SectionLabel>
          <SectionTitle>Where everyone is on the path</SectionTitle>
          <SectionDesc>
            Career paths aren't straight lines. People jump tracks — a DevOps engineer
            becomes an SRE, an SRE moves to Platform. You keep your seniority and your
            skills. The track changes, the experience stays.
          </SectionDesc>

          <TeamCareerGrid>
            {teamMembers.map((member, i) => (
              <TeamCard key={i}>
                <TeamName>{member.name}</TeamName>
                <TeamRole>{member.role} — {member.track} TRACK</TeamRole>
                <TeamPath>
                  {member.path.map((step, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <PathArrow>→</PathArrow>}
                      {step.switched ? (
                        <TrackSwitch>{step.track} {step.grade}</TrackSwitch>
                      ) : (
                        <PathNode current={j === member.path.length - 1}>
                          {step.grade}
                        </PathNode>
                      )}
                    </React.Fragment>
                  ))}
                </TeamPath>
                {member.certs.length > 0 && (
                  <TeamCerts>
                    {member.certs.map((c, j) => <MiniCert key={j}>{c}</MiniCert>)}
                  </TeamCerts>
                )}
                {member.note && (
                  <GrowthText style={{ marginTop: "12px", fontSize: "13px", fontStyle: "italic" }}>
                    {member.note}
                  </GrowthText>
                )}
              </TeamCard>
            ))}
          </TeamCareerGrid>
        </Container>
      </Section>

      <LaneViewer />

      <CertificationsGrid
        user={user}
        signIn={signIn}
        proofs={proofs}
        certStatus={certStatus}
        openCertProof={openCertProof}
        certProofTab={certProofTab}
        setCertProofTab={setCertProofTab}
        certProofUrl={certProofUrl}
        setCertProofUrl={setCertProofUrl}
        certUploading={certUploading}
        onCertStatusToggle={handleCertStatusToggle}
        onCertProofToggle={handleCertProofToggle}
        onCertSaveLink={handleCertSaveLink}
        onCertUploadFile={handleCertUploadFile}
        onCertRemoveProof={handleCertRemoveProof}
      />

      <SkillsInventory
        user={user}
        signIn={signIn}
        signOut={signOut}
        saveMsg={saveMsg}
        skills={skills}
        setSkills={setSkills}
        certs={certs}
        openProof={openProof}
        proofTab={proofTab}
        setProofTab={setProofTab}
        proofUrl={proofUrl}
        setProofUrl={setProofUrl}
        uploading={uploading}
        profilePublic={profilePublic}
        copyHint={copyHint}
        emailMuted={emailMuted}
        onToggleEmailMute={handleToggleEmailMute}
        onTogglePublic={handleTogglePublic}
        onCopyShareLink={handleCopyShareLink}
        onProofToggle={handleProofToggle}
        onSaveLink={handleSaveLink}
        onUploadFile={handleUploadFile}
        onRemoveCert={handleRemoveCert}
      />

      {/* Role Jumping + Growth Philosophy */}
      <Section>
        <Container>
          <GrowthBox>
            <SectionLabel>PHILOSOPHY</SectionLabel>
            <GrowthTitle>Tracks aren't cages</GrowthTitle>
            <GrowthText>
              Your title belongs to you, not to a track. A Senior DevOps engineer who
              moves to SRE stays senior — architecture, incident response, and
              operational judgment carry across.
            </GrowthText>
            <GrowthText>
              Progress reviews are quarterly, with notes, action items, and
              follow-through — not an annual ritual no one remembers.
            </GrowthText>
            <GrowthText>
              <Honest>
                The best thing we can do is help you outgrow yourself.
              </Honest>
            </GrowthText>
          </GrowthBox>
        </Container>
      </Section>
    </>
  );
};

export default CareerPath;
